import { Button } from '@lms/ui/button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@lms/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@lms/ui/popover';
import { cn } from '@lms/ui/utils';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';
import type React from 'react';
import { useLocale, useTranslations } from 'use-intl';
import { isLocale } from '@/features/locale/supported-languages';
import { useLocaleControl } from '@/i18n/locale-provider';

// Each language is named in itself, so a speaker can find their own.
const languages = [
  { label: 'English', value: 'en' },
  { label: 'Svenska', value: 'sv' },
  { label: 'Norsk', value: 'no' },
  { label: 'Dansk', value: 'dk' },
  { label: 'Deutsch', value: 'de' },
  { label: 'Français', value: 'fr' },
  { label: 'Suomi', value: 'fi' },
  { label: 'Português', value: 'pt' },
  { label: 'Polski', value: 'pl' },
] as const;

const LanguageSelector: React.FC<{
  className?: string;
  variant?: 'default' | 'folded';
}> = ({ className, variant }) => {
  const t = useTranslations('global');
  const activeLanguage = useLocale();
  const { setLocale } = useLocaleControl();

  const onSwitch = (locale: string) => {
    if (isLocale(locale)) setLocale(locale);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className={cn(
            variant !== 'folded'
              ? 'w-[200px] justify-between'
              : 'w-4 justify-center overflow-hidden p-2',
            !activeLanguage && 'text-muted-foreground',
            className,
          )}
        >
          <span>
            <Globe
              className={cn(
                variant !== 'folded' ? 'mr-2' : '',
                'text-primary inline-block !size-5 align-top',
              )}
            />
            {variant !== 'folded' &&
              (activeLanguage
                ? languages.find(
                    (language) => language.value === activeLanguage,
                  )?.label
                : t('select_language'))}
          </span>
          {variant !== 'folded' && (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-[200px] p-0', className)}>
        <Command>
          <CommandInput placeholder={t('select_language')} />
          <CommandList>
            <CommandGroup>
              {languages.map((language) => (
                <CommandItem
                  value={language.value}
                  keywords={[language.label]}
                  key={language.value}
                  onSelect={onSwitch}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      language.value === activeLanguage
                        ? 'opacity-100'
                        : 'opacity-0',
                    )}
                  />
                  {language.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

LanguageSelector.displayName = 'LanguageSelector';

export { LanguageSelector };
